import { useEffect, useState } from 'react'
import {
	fetchComplianceEvidence,
	fetchCounsellorInsights,
	fetchStudentInsights,
} from 'api/reports'
import { JURISDICTION_REPORTS } from '../data/reportFixtures'
import {
	PERIOD_ALL_TIME,
	PERIOD_ACADEMIC_YEAR,
	buildInsightsDateParams,
	isSelectableReportPeriod,
} from '../utils/reportsPeriods'

const emptySummary = () => ({
	assessedPct: 0,
	sessions: 0,
	ready: 0,
	evidence: 0,
})

/**
 * Live hub metrics for the three report cards (no fixture fallback).
 * Student insights follow APPLIED period; counsellor summary stays all-time.
 */
export const useReportsHub = ({
	period,
	jurisdictionId = 'uae-dubai',
	customFrom,
	customTo,
}) => {
	const [state, setState] = useState({
		status: 'loading',
		summary: emptySummary(),
		error: null,
	})

	useEffect(() => {
		let active = true
		const profile = JURISDICTION_REPORTS[jurisdictionId] || JURISDICTION_REPORTS['uae-dubai']
		const periodKey = isSelectableReportPeriod(period) ? period : PERIOD_ACADEMIC_YEAR
		const studentDateParams = buildInsightsDateParams({
			period: periodKey,
			customFrom,
			customTo,
		})

		if (!studentDateParams) {
			setState(prev => ({
				...prev,
				status: 'idle',
				error: null,
			}))
			return () => {
				active = false
			}
		}

		setState(prev => ({ ...prev, status: 'loading', error: null }))

		const load = async () => {
			try {
				const [studentData, counsellorData, evidenceData] = await Promise.all([
					fetchStudentInsights({ ...studentDateParams, grade: 'all' }),
					fetchCounsellorInsights({ period: PERIOD_ALL_TIME }),
					fetchComplianceEvidence({ jurisdiction: profile.id }),
				])

				if (!active) return

				const roster = Number(studentData?.roster?.total || 0)
				const assessed = Number(
					studentData?.complianceLive?.assessed ??
						studentData?.doneSnapshot?.withAssessment ??
						0,
				)
				const sessions = Number(counsellorData?.summaryTeam?.sessions || 0)
				const collected = evidenceData?.evidenceCollected || {}
				const ready = profile.requirements.filter(row => Boolean(collected[row.id])).length

				setState({
					status: 'success',
					summary: {
						assessedPct: roster ? Math.round((assessed / roster) * 100) : 0,
						sessions,
						ready,
						evidence: profile.requirements.length,
					},
					error: null,
				})
			} catch (err) {
				if (!active) return
				setState({
					status: 'error',
					summary: {
						...emptySummary(),
						evidence: profile.requirements.length,
					},
					error:
						err?.response?.data?.error ||
						err?.message ||
						'Failed to load report summary',
				})
			}
		}

		load()
		return () => {
			active = false
		}
	}, [period, jurisdictionId, customFrom, customTo])

	return state
}
